// Wraps a local IndexedDB store so its writes also mirror to the server, WITHOUT
// changing how callers use it: the return value is a plain `IDBStore<T>`, so
// `collection-store` / `floor-plan-store` swap `createIDBStore(...)` for
// `createSyncedStore(...)` and nothing downstream (the dock, save-session) moves.
//
// Offline-first: the local store stays the source of truth. Reads are purely
// local (instant, work offline); writes hit local first, then — only once a
// backend is connected — queue an outbox entry for the controller to push. Until
// sign-in (`controller.enabled === false`) this is a byte-for-byte passthrough
// to the local store.

import type { IDBStore } from "../model/idb-store";
import type { SyncController } from "./controller";

export function createSyncedStore<T extends { id: string; updatedAt: number }>(
  table: string,
  local: IDBStore<T>,
  controller: SyncController,
  opts?: { localOnlyFields?: string[] },
): IDBStore<T> {
  controller.register(table, local, opts);

  return {
    // Reads: local only.
    list: () => local.list(),
    listKeys: () => local.listKeys(),
    get: (id) => local.get(id),

    // Writes: local first (keeps today's instant, offline-capable save), then
    // mirror. enqueue is fire-and-forget — a queue failure must not fail the
    // local save the user already sees as done.
    async put(item) {
      const key = await local.put(item);
      if (controller.enabled) void controller.enqueue(table, "put", item.id, item.updatedAt);
      return key;
    },
    async remove(id) {
      const r = await local.remove(id);
      // No record to read at flush time, so stamp the tombstone now.
      if (controller.enabled) void controller.enqueue(table, "delete", id, Date.now());
      return r;
    },
  };
}
